/**
 * Returns human-readable "actual result" for a market from match score.
 * Used in 1v1 history to show "Result: ..." next to You/Friend picks.
 * Uses MARKET_IDS and HANDICAP_MARKET_ID_TO_LINE from matchDetails.constants as single source of truth.
 */

import {
  MARKET_IDS,
  HANDICAP_MARKET_ID_TO_LINE,
  getHandicapLineFromLegacyValue,
} from '../../create/constants/matchDetails.constants';

function europeanHandicapOutcome(homeScore: number, awayScore: number, line: number): 'Home' | 'Draw' | 'Away' {
  const homeAdj = homeScore + line;
  const awayAdj = awayScore;
  if (homeAdj > awayAdj) return 'Home';
  if (awayAdj > homeAdj) return 'Away';
  return 'Draw';
}

/** Format handicap correct outcome per line: "Home -1", "Draw -1", "Away +1" (matches picker display). */
function formatHandicapCorrectOutcome(line: number, outcome: 'Home' | 'Draw' | 'Away'): string {
  if (line === 0) return `${outcome} 0`;
  const displayLine = outcome === 'Away' ? -line : line;
  const sign = displayLine > 0 ? '+' : '';
  return `${outcome} ${sign}${displayLine}`;
}

function outcomeFromScore(homeScore: number, awayScore: number): 'Home' | 'Draw' | 'Away' {
  if (homeScore > awayScore) return 'Home';
  if (awayScore > homeScore) return 'Away';
  return 'Draw';
}

/**
 * Get actual result string for a market from final score.
 * Returns empty string if score is missing or market not supported.
 * For HT/FT and Highest Scoring Half, pass optional half-time scores.
 */
export function getActualResultForMarket(
  homeScore: number | null,
  awayScore: number | null,
  marketId: number,
  halfTimeHomeScore?: number | null,
  halfTimeAwayScore?: number | null
): string {
  if (homeScore === null || awayScore === null) return '';
  const total = homeScore + awayScore;
  const outcome = outcomeFromScore(homeScore, awayScore);

  switch (marketId) {
    case MARKET_IDS.MATCH_WINNER:
    case MARKET_IDS.DOUBLE_CHANCE:
      return `${outcome} (${homeScore}-${awayScore})`;
    case MARKET_IDS.FIRST_HALF_WINNER:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `${outcomeFromScore(halfTimeHomeScore, halfTimeAwayScore)} (${halfTimeHomeScore}-${halfTimeAwayScore})`;
    case MARKET_IDS.SECOND_HALF_WINNER:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `${outcomeFromScore(homeScore - halfTimeHomeScore, awayScore - halfTimeAwayScore)}`;
    case MARKET_IDS.BTTS:
      return homeScore > 0 && awayScore > 0 ? 'Yes' : 'No';
    case MARKET_IDS.BTTS_FIRST_HALF:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return halfTimeHomeScore > 0 && halfTimeAwayScore > 0 ? 'Yes' : 'No';
    case MARKET_IDS.BTTS_SECOND_HALF:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return (homeScore - halfTimeHomeScore) > 0 && (awayScore - halfTimeAwayScore) > 0 ? 'Yes' : 'No';
    case MARKET_IDS.WIN_BOTH_HALVES: {
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      const htWin = outcomeFromScore(halfTimeHomeScore, halfTimeAwayScore);
      const s2Win = outcomeFromScore(homeScore - halfTimeHomeScore, awayScore - halfTimeAwayScore);
      if (htWin === 'Home' && s2Win === 'Home') return 'Home wins both';
      if (htWin === 'Away' && s2Win === 'Away') return 'Away wins both';
      return 'No';
    }
    case MARKET_IDS.OVER_UNDER_15:
      return `Total ${total} goals`;
    case MARKET_IDS.OVER_UNDER_25:
      return `Total ${total} goals`;
    case MARKET_IDS.OVER_UNDER_35:
      return `Total ${total} goals`;
    case MARKET_IDS.OVER_UNDER_05_2ND_HALF:
    case MARKET_IDS.OVER_UNDER_15_2ND_HALF:
    case MARKET_IDS.OVER_UNDER_25_2ND_HALF: {
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      const s2Total = total - (halfTimeHomeScore + halfTimeAwayScore);
      return `Total ${s2Total} 2nd half goals`;
    }
    case MARKET_IDS.EXACT_GOALS:
      return `${total} goals`;
    case MARKET_IDS.ODD_EVEN:
      return total % 2 === 0 ? 'Even' : 'Odd';
    case MARKET_IDS.CORRECT_SCORE:
      return `${homeScore}-${awayScore}`;
    case MARKET_IDS.HALF_TIME_CORRECT_SCORE:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `${halfTimeHomeScore}-${halfTimeAwayScore}`;
    case MARKET_IDS.HANDICAP_RESULT:
      return `Result ${homeScore}-${awayScore}`;
    case MARKET_IDS.HALF_TIME_FULL_TIME:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `HT/FT: ${outcomeFromScore(halfTimeHomeScore, halfTimeAwayScore)}/${outcome}`;
    case MARKET_IDS.HIGHEST_SCORING_HALF:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      const firstHalfTotal = halfTimeHomeScore + halfTimeAwayScore;
      const secondHalfTotal = total - firstHalfTotal;
      if (firstHalfTotal > secondHalfTotal) return '1st Half';
      if (secondHalfTotal > firstHalfTotal) return '2nd Half';
      return 'Draw';
    default:
      if (HANDICAP_MARKET_ID_TO_LINE[marketId] !== undefined) {
        const line = HANDICAP_MARKET_ID_TO_LINE[marketId];
        const handicapOutcome = europeanHandicapOutcome(homeScore, awayScore, line);
        return `Handicap ${line >= 0 ? '+' : ''}${line}: ${handicapOutcome}`;
      }
      return '';
  }
}

/** Placeholder when correct outcome cannot be derived (e.g. corners – no data in result). */
export const CORRECT_OUTCOME_UNAVAILABLE = '—';

/** Shown when total equals O/U line (push – neither Over nor Under is correct). */
export const CORRECT_OUTCOME_PUSH = 'Push';

function overUnderLabel(total: number, line: number): string {
  if (total > line) return `Over ${line}`;
  if (total < line) return `Under ${line}`;
  return CORRECT_OUTCOME_PUSH;
}

/**
 * Short label for correct outcome on finished pick rows (e.g. "Draw", "1:0", "2nd Half").
 * Returns empty string when score is missing or market not supported.
 * For legacy Handicap Result (market 9), pass pickValue (e.g. "Home -1") to derive line.
 */
export function getCorrectOutcomeLabel(
  homeScore: number | null,
  awayScore: number | null,
  marketId: number,
  halfTimeHomeScore?: number | null,
  halfTimeAwayScore?: number | null,
  pickValue?: string
): string {
  if (homeScore === null || awayScore === null) return '';
  const outcome = outcomeFromScore(homeScore, awayScore);

  const total = homeScore + awayScore;
  const firstHalfTotal =
    halfTimeHomeScore != null && halfTimeAwayScore != null
      ? halfTimeHomeScore + halfTimeAwayScore
      : null;

  switch (marketId) {
    case MARKET_IDS.MATCH_WINNER:
    case MARKET_IDS.DOUBLE_CHANCE:
      return outcome;
    case MARKET_IDS.FIRST_HALF_WINNER:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return outcomeFromScore(halfTimeHomeScore, halfTimeAwayScore);
    case MARKET_IDS.SECOND_HALF_WINNER:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return outcomeFromScore(homeScore - halfTimeHomeScore, awayScore - halfTimeAwayScore);
    case MARKET_IDS.BTTS:
      return homeScore > 0 && awayScore > 0 ? 'Yes' : 'No';
    case MARKET_IDS.OVER_UNDER_15:
      return overUnderLabel(total, 1.5);
    case MARKET_IDS.OVER_UNDER_25:
      return overUnderLabel(total, 2.5);
    case MARKET_IDS.OVER_UNDER_35:
      return overUnderLabel(total, 3.5);
    case MARKET_IDS.EXACT_GOALS:
      return `${total} goals`;
    case MARKET_IDS.ODD_EVEN:
      return total % 2 === 0 ? 'Even' : 'Odd';
    case MARKET_IDS.CORRECT_SCORE:
      return `${homeScore}:${awayScore}`;
    case MARKET_IDS.HALF_TIME_CORRECT_SCORE:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `${halfTimeHomeScore}:${halfTimeAwayScore}`;
    case MARKET_IDS.HIGHEST_SCORING_HALF:
      if (firstHalfTotal == null) return '';
      const secondHalfTotal = total - firstHalfTotal;
      if (firstHalfTotal > secondHalfTotal) return '1st Half';
      if (secondHalfTotal > firstHalfTotal) return '2nd Half';
      return 'Draw';
    case MARKET_IDS.HALF_TIME_FULL_TIME:
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      return `${outcomeFromScore(halfTimeHomeScore, halfTimeAwayScore)}/${outcome}`;
    case MARKET_IDS.CLEAN_SHEET_HOME:
      return awayScore === 0 ? 'Yes' : 'No';
    case MARKET_IDS.CLEAN_SHEET_AWAY:
      return homeScore === 0 ? 'Yes' : 'No';
    case MARKET_IDS.OVER_UNDER_05_1ST_HALF:
      if (firstHalfTotal == null) return '';
      return overUnderLabel(firstHalfTotal, 0.5);
    case MARKET_IDS.OVER_UNDER_15_1ST_HALF:
      if (firstHalfTotal == null) return '';
      return overUnderLabel(firstHalfTotal, 1.5);
    case MARKET_IDS.OVER_UNDER_25_1ST_HALF:
      if (firstHalfTotal == null) return '';
      return overUnderLabel(firstHalfTotal, 2.5);
    case MARKET_IDS.OVER_UNDER_05_2ND_HALF:
    case MARKET_IDS.OVER_UNDER_15_2ND_HALF:
    case MARKET_IDS.OVER_UNDER_25_2ND_HALF: {
      if (halfTimeHomeScore == null || halfTimeAwayScore == null) return '';
      const s2Total = total - (halfTimeHomeScore + halfTimeAwayScore);
      return overUnderLabel(s2Total, marketId === MARKET_IDS.OVER_UNDER_05_2ND_HALF ? 0.5 : marketId === MARKET_IDS.OVER_UNDER_15_2ND_HALF ? 1.5 : 2.5);
    }
    case MARKET_IDS.CORNERS:
      return CORRECT_OUTCOME_UNAVAILABLE; // Actual corners not in match result
    case MARKET_IDS.CARDS:
      return CORRECT_OUTCOME_UNAVAILABLE; // Actual cards not in match result
    case MARKET_IDS.HANDICAP_RESULT: {
      const line = getHandicapLineFromLegacyValue(pickValue);
      if (line === null) return '';
      const outcome = europeanHandicapOutcome(homeScore, awayScore, line);
      return formatHandicapCorrectOutcome(line, outcome);
    }
    default:
      if (HANDICAP_MARKET_ID_TO_LINE[marketId] !== undefined) {
        const line = HANDICAP_MARKET_ID_TO_LINE[marketId];
        const outcome = europeanHandicapOutcome(homeScore, awayScore, line);
        return formatHandicapCorrectOutcome(line, outcome);
      }
      return '';
  }
}
